import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, ImageOff, Plus, RotateCcw, Save, Trash2, Upload } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadPageImage } from '@/lib/customPageApi';
import { useProducts } from '@/contexts/ProductsContext';
import { Link } from 'react-router-dom';
import { SHOP_CONFIG_KEY, defaultShopConfig, parseShopConfig, useShopMembers, type Lang, type ShopConfig, type ShopEntry, type Texts } from '@/lib/shopConfig';

// Admin > Website > Home Page > Shop categories & concerns: the round
// "Shop by category" / "Shop by concern" shortcuts of the new design (Home
// shop, header mega menu, phone drawer). Each circle has a photo, a name per
// language, an order and a show/hide switch; which products it shows is
// chosen on each product's page. Saved as one JSON row in rxk_site_content.

type Kind = 'categories' | 'concerns';
const LANGS: [Lang, string][] = [
  ['en', 'English'],
  ['bn', 'বাংলা'],
  ['ko', '한국어'],
];
const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'item';

function PhotoPick({ value, fallback, round, onChange }: { value: string; fallback: string; round?: boolean; onChange: (url: string) => void }) {
  const { push } = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadPageImage(file));
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setBusy(false);
    }
  };
  const src = value || fallback;
  return (
    <div className="flex flex-col items-center gap-2 w-28 flex-none">
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={`group relative w-24 h-24 ${round ? 'rounded-full' : 'rounded-xl'} overflow-hidden bg-surface-muted ring-1 ring-border-subtle hover:ring-2 hover:ring-primary-700 transition cursor-pointer`}
        aria-label="Change photo"
      >
        {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-6 h-6 m-auto text-ink-faint" />}
        <span className="absolute inset-0 grid place-items-center bg-black/45 text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition">
          {busy ? 'Uploading…' : <Upload className="w-5 h-5" />}
        </span>
      </button>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} />
      {value ? (
        <button type="button" className="text-[11px] text-ink-faint hover:text-error cursor-pointer" onClick={() => onChange('')}>
          Use product photo
        </button>
      ) : (
        <span className="text-[11px] text-ink-faint">{fallback ? 'Product photo' : 'No photo'}</span>
      )}
    </div>
  );
}

export function ShopCirclesEditor() {
  const { push } = useToast();
  const { products, routineSet } = useProducts();
  const [saved, setSaved] = useState<string | null>(null);
  const [cfg, setCfg] = useState<ShopConfig | null>(null);
  const [kind, setKind] = useState<Kind>('categories');
  const [lang, setLang] = useState<Lang>('en');
  const [saving, setSaving] = useState(false);

  const members = useShopMembers();
  const membersOf = (key: string) => members.filter((m) => m[kind].includes(key));

  useEffect(() => {
    if (cfg) return;
    supabase
      .from('rxk_site_content')
      .select('value')
      .eq('key', SHOP_CONFIG_KEY)
      .eq('locale', 'en')
      .maybeSingle()
      .then(({ data }) => {
        const raw = (data as { value: string } | null)?.value ?? '';
        const v = parseShopConfig(raw) ?? defaultShopConfig();
        setCfg(v);
        setSaved(JSON.stringify(v));
      });
  }, [cfg]);

  if (!cfg) return <Spinner />;
  const dirty = JSON.stringify(cfg) !== saved;
  const list = cfg[kind];
  const setList = (next: ShopEntry[]) => setCfg({ ...cfg, [kind]: next });
  const patch = (i: number, p: Partial<ShopEntry>) => setList(list.map((e, j) => (j === i ? { ...e, ...p } : e)));
  const setText = (i: number, field: 'label' | 'long', value: string) => patch(i, { [field]: { ...(list[i][field] ?? {}), [lang]: value } as Texts });
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };
  const add = () => {
    const base = kind === 'categories' ? 'category' : 'concern';
    let key = base;
    for (let n = 2; list.some((e) => e.key === key); n++) key = `${base}-${n}`;
    setList([...list, { key, label: { en: '' }, ...(kind === 'concerns' ? { long: { en: '' } } : {}), image: '' }]);
  };
  const remove = (i: number) => {
    if (!window.confirm(`Delete “${list[i].label.en || list[i].key}”?`)) return;
    setList(list.filter((_, j) => j !== i));
  };
  const reset = () => {
    if (!window.confirm('Go back to the default circles? Your changes here are replaced (nothing is saved until you press Save).')) return;
    setCfg(defaultShopConfig());
  };
  const save = async () => {
    // Give new circles a stable key from their English name.
    const fix = (l: ShopEntry[]) =>
      l.map((e) => {
        if (!/^(category|concern)(-\d+)?$/.test(e.key) || !e.label.en) return e;
        let key = slugify(e.label.en);
        for (let n = 2; l.some((o) => o !== e && o.key === key); n++) key = `${slugify(e.label.en)}-${n}`;
        return { ...e, key };
      });
    const next = { ...cfg, categories: fix(cfg.categories), concerns: fix(cfg.concerns) };
    if ([...next.categories, ...next.concerns].some((e) => !e.label.en?.trim())) {
      push('error', 'Every circle needs an English name.');
      return;
    }
    setSaving(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const value = JSON.stringify(next);
    const { error } = await supabase.from('rxk_site_content').upsert({ key: SHOP_CONFIG_KEY, locale: 'en', value, updated_by: user?.id ?? null }, { onConflict: 'key,locale' });
    setSaving(false);
    if (error) {
      push('error', error.message);
      return;
    }
    setCfg(next);
    setSaved(value);
    push('success', 'Saved. The Home shop, header menu and phone menu now use these circles.');
  };

  const visible = list.filter((e) => !e.hidden && membersOf(e.key).length);
  const circle = (src: string, label: string, round = true, key?: string) => (
    <div key={key ?? label} className="flex flex-col items-center gap-2 w-20 flex-none">
      <span className={`w-16 h-16 ${round ? 'rounded-full' : 'rounded-xl'} overflow-hidden bg-surface-muted ring-1 ring-border-subtle`}>
        {src && <img src={src} alt="" className="w-full h-full object-cover" />}
      </span>
      <span className="text-[11.5px] text-ink text-center leading-tight line-clamp-2">{label || '—'}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Shop categories & concerns"
          subtitle="The round shortcuts above the Home shop. The same lists fill the header's Products menu and the phone menu."
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={reset}>
                <RotateCcw className="w-3.5 h-3.5" /> Defaults
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty || saving} loading={saving}>
                <Save className="w-3.5 h-3.5" /> {dirty ? 'Save changes' : 'Saved'}
              </Button>
            </div>
          }
        />
        <CardBody className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                ['categories', `Shop by category (${cfg.categories.length})`],
                ['concerns', `Shop by concern (${cfg.concerns.length})`],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors ${kind === k ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
            <span className="ml-auto flex items-center gap-1 text-[12px] text-ink-faint">
              Names in
              {LANGS.map(([l, label]) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1 rounded-md cursor-pointer ${lang === l ? 'bg-ink text-white' : 'hover:bg-surface-muted'}`}
                >
                  {label}
                </button>
              ))}
            </span>
          </div>

          <div className="rounded-xl border border-border-subtle bg-surface-muted/50 p-4">
            <p className="text-[10.5px] tracking-[0.14em] uppercase font-semibold text-ink-faint mb-3">Preview on the Home page</p>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {kind === 'categories' && circle(cfg.allImage || routineSet?.image || '', 'All', true, '__all')}
              {visible.map((e) => circle(e.image || membersOf(e.key)[0]?.image || '', e.label[lang] || e.label.en || '', true, e.key))}
            </div>
            {list.some((e) => e.hidden || !membersOf(e.key).length) && (
              <p className="text-[11.5px] text-ink-faint mt-2">Hidden circles and circles with no products are not shown.</p>
            )}
          </div>

          {kind === 'categories' && (
            <div className="flex items-center gap-4 border border-border-subtle rounded-xl p-3">
              <PhotoPick value={cfg.allImage ?? ''} fallback={routineSet?.image ?? ''} round onChange={(allImage) => setCfg({ ...cfg, allImage })} />
              <div>
                <p className="text-sm font-semibold text-ink">“All” circle</p>
                <p className="text-xs text-ink-faint">Always first and shows every product. Only its photo can change.</p>
              </div>
            </div>
          )}

          {list.map((e, i) => (
            <div key={i} className={`border rounded-xl p-3 flex flex-col sm:flex-row gap-4 ${e.hidden ? 'border-dashed border-border-subtle opacity-60' : 'border-border-subtle'}`}>
              <PhotoPick value={e.image} fallback={membersOf(e.key)[0]?.image ?? ''} round onChange={(image) => patch(i, { image })} />
              <div className="flex-1 min-w-0 space-y-3">
                <div className={`grid gap-3 ${kind === 'concerns' ? 'sm:grid-cols-2' : ''}`}>
                  <Field label={`Name (${LANGS.find(([l]) => l === lang)?.[1]})`}>
                    <Input value={e.label[lang] ?? ''} placeholder={lang === 'en' ? 'e.g. Serums' : e.label.en} onChange={(ev) => setText(i, 'label', ev.target.value)} />
                  </Field>
                  {kind === 'concerns' && (
                    <Field label={`Longer name for the filter and menu (${LANGS.find(([l]) => l === lang)?.[1]})`}>
                      <Input value={e.long?.[lang] ?? ''} placeholder={e.long?.en || e.label[lang] || e.label.en} onChange={(ev) => setText(i, 'long', ev.target.value)} />
                    </Field>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-text-secondary mb-2">
                    Products here ({membersOf(e.key).length}) · <span className="font-normal text-ink-faint">choose on each product's page: Products → product → Shop circles</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {membersOf(e.key).length ? (
                      membersOf(e.key).map((m) => (
                        <Link
                          key={m.slug}
                          to={`/admin/products/${m.sku}`}
                          className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-border-subtle text-[12.5px] text-ink hover:border-primary-700 hover:text-primary-800 transition"
                        >
                          <img src={m.image} alt="" className="w-6 h-6 rounded-full object-cover bg-surface-muted" />
                          {m.name}
                        </Link>
                      ))
                    ) : (
                      <span className="text-[12.5px] text-ink-faint">No products yet, so this circle is not shown.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex sm:flex-col gap-1 justify-end sm:justify-start">
                <Button size="sm" variant="ghost" aria-label="Move up" onClick={() => move(i, -1)} disabled={i === 0}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" aria-label="Move down" onClick={() => move(i, 1)} disabled={i === list.length - 1}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost" aria-label={e.hidden ? 'Show' : 'Hide'} onClick={() => patch(i, { hidden: !e.hidden })}>
                  {e.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => remove(i)}>
                  <Trash2 className="w-3.5 h-3.5 text-error" />
                </Button>
              </div>
            </div>
          ))}

          <Button variant="secondary" onClick={add}>
            <Plus className="w-4 h-4" /> Add {kind === 'categories' ? 'category' : 'concern'}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
